import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import PropTypes from 'prop-types';
import {useState} from 'react';

import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import './EditPatientStatus.css';
import useError from '../../hooks/useError';
import {getPatientStatuses} from '../../helpers/selectOptions';
import {useGetCasesQuery} from '../../redux/services/caseApi';
import {useUpdatePatientStatusMutation} from '../../redux/services/patientStatusApi';

import CustomSelect from '../common/CustomSelect';
import {getPatientStatusOptions} from "./PatientStatusFilter";
import CasesTable from '../cases/CasesTable';
import {ChevronDownIcon} from "../../styles/icons";

const handleChange = ({
  put, 
  id, 
  createError, 
  setValue, 
  setSuccess, 
  setLoading,
  setCaseDialogOpen,
}) => (
  async e => {
    setLoading(true);
    const status = get(e, 'target.value');
    const res = await put({id, body: {status}});
    setLoading(false);
    if (res.error) {
      setSuccess(false);
      return createError(new Error('Error updating status.'));
    }
    setValue(status);
    setSuccess(true);
    if (['Lost', 'Goals Met', 'Referred Out'].includes(status)) {
      setCaseDialogOpen(true);
    }
  }
);

const CaseStatusDialog = props => {
  const {patientId} = props;
  const caseQuery = useGetCasesQuery(
    {patient_id: patientId, status: 'ACTIVE', limit: 50, skip: 0},
    {skip: !props.patientId || !props.isOpen}
  );
  const cases = caseQuery?.data?.data || [];
  const isOpen = cases.length > 0 && props.isOpen;
  return (
    <Dialog open={isOpen} onClose={props.onClose}>
      <DialogTitle>
        <Typography variant='h2' align='center'>
          Review Active Cases
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant='body1'>
            This patient still has the following active cases associated with your care.
            Before changing the patient status, make sure the case status is up to date.
          </Typography>
          <Typography variant='body1' color='text.light'>
            * A case may stay active if another provider has ongoing care planned for patient.
          </Typography>
          <CasesTable
            columns={[
              'status', 
              'providers', 
              'billing_category', 
              'diagnosis_categories'
            ]}
            rows={cases.map(c => ({data: c, buttons: []}))}
            count={cases.length || 0}
            pageIndex={0}
            pageSize={50} 
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditPatientStatus = props => {
  const status = get(props, 'patient.patient_status.status');
  const [value, setValue] = useState(status);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [put] = useUpdatePatientStatusMutation();
  const {create: createError} = useError();
  const id = get(props, 'patient.patient_status.id');
  const {color, Icon} = getPatientStatusOptions(camelCase(value));
  const onChange = handleChange({
    put,
    id,
    createError,
    setSuccess,
    setValue,
    setLoading,
    setCaseDialogOpen,
  });
  return (
    <Stack direction='row' spacing={0.5} alignItems='center' className='EditPatientStatus'>
      <CaseStatusDialog 
        patientId={props.patient.id} 
        isOpen={caseDialogOpen}
        onClose={() => setCaseDialogOpen(false)}
      />
      <Icon style={{color, fontSize: '1.75em', opacity: status === 'Scheduled' || loading ? 0.6 : 1}}/>
      <CustomSelect
        size='small'
        style={{width: '115px'}}
        disableClearable
        disabled={status === 'Scheduled' || loading}
        defaultValue={status}
        value={value}
        popupIcon={<ChevronDownIcon fill='#6F767E'/>}
        options={
          getPatientStatuses()
            .filter(s => s.value !== 'Scheduled' || status === 'Scheduled')
        }
        onChange={onChange}
      />
      {
        success
        ? <CheckIcon color='success'/>
        : null
      }
      {
        success === false
        ? <CloseIcon color='error'/>
        : null
      }
    </Stack>
  );
};

EditPatientStatus.propTypes = {
  patient: PropTypes.object,
};

export default EditPatientStatus;
